// Transaction Wizard (obj_type "U") — the shared contract.
//
// A wizard is a guided, step-by-step FACE on an existing transaction (T): the
// wizard owns the steps, the branching and the presentation; the transaction
// owns the data, the rules and the save. Design: docs/plans/transaction-wizard.md.
//
// This file is the ONE definition every surface agrees on:
//   * the model shape (as saved in Metadata_Model/wizard_models/model_<obj>.json
//     and as the server's normaliser returns it — twasta_runtime_engine/wizard/
//     model.py is authoritative; keep the two in step);
//   * the wire payloads of the `wizard_*` engine operations and `validate_tree`;
//   * the host seam (`WizardHost`) each runtime implements to mount the runner.
//
// Every request the runner makes for a wizard carries `wizard: WizardVia` in the
// request BODY (beside `payload`, never inside it). The host's gate then
// authorizes it against the WIZARD's own grant (run / add / edit) instead of the
// base transaction's, and the engine bounds the save with the write guard.

import type { ReactNode } from 'react'
import type {
  AssistantActionRequest, OpenActionBinding, OpenActionRequest, TransactionModel,
} from '../txn/types'
import type { TxnScreenRuntime } from '../txn/screen/contract'
import type { DmsClient } from '../dms/contracts'

// ── Model ────────────────────────────────────────────────────────────────

/** `fill` — launched from an action button of its OWN base transaction, the
 *  wizard works on that screen's open buffer and hands back what it changed
 *  (`WizardFillResult`); it never saves (docs/plans/wizard-external-website-step.md §7). */
export type WizardMode = 'add' | 'edit' | 'add_or_edit' | 'fill'

export type WizardStepKind =
  | 'info' | 'select' | 'input' | 'scan' | 'grid' | 'confirm' | 'fetch_url'
  | 'auto' | 'branch' | 'load' | 'repeat' | 'save' | 'show' | 'print'
  | 'action' | 'call' | 'handoff' | 'function' | 'redirect' | 'sign' | 'send'

export const WIZARD_STEP_KINDS: readonly WizardStepKind[] = [
  'info', 'select', 'input', 'scan', 'grid', 'confirm', 'fetch_url', 'auto',
  'branch', 'load', 'repeat', 'save', 'show', 'print', 'action', 'call', 'handoff',
  'function', 'redirect', 'sign', 'send',
]

export type WizardOpKind =
  | 'set' | 'item_change' | 'add_line' | 'complete_line' | 'delete_line'
  | 'match_line' | 'lookup' | 'fetch_lines' | 'recalc_lines' | 'run_action'
  | 'validate' | 'fetch_url' | 'goto' | 'message'

export const WIZARD_OP_KINDS: readonly WizardOpKind[] = [
  'set', 'item_change', 'add_line', 'complete_line', 'delete_line', 'match_line',
  'lookup', 'fetch_lines', 'recalc_lines', 'run_action', 'validate', 'fetch_url',
  'goto', 'message',
]

/** `"step_id"`, `{finish: true}`, or ordered cases (first true wins; a case
 *  without `when` is the default). */
export type WizardNext =
  | string
  | { finish: true }
  | Array<{ when?: string; goto: string } | { when?: string; finish: true }>

export interface WizardInput {
  name: string
  type?: 'string' | 'number' | 'date' | 'boolean'
  label?: string
  required?: boolean
  /** `header.COL` / `line.COL` / `var.NAME` — set in order after defaults. */
  target?: string
  default?: unknown
  /** A CALLER may lock it (`lock_inputs`) — presentation only. */
  lockable?: boolean
}

export interface WizardVar {
  name: string
  type?: 'string' | 'number' | 'date' | 'boolean'
  default?: unknown
}

/** One operation of a `then` / `do` list (design §6.13). Free-form keys per op. */
export interface WizardOp {
  op: WizardOpKind
  /** set */
  target?: string
  value?: unknown
  expr?: string
  item_change?: boolean
  /** item_change */
  field?: string
  /** add_line */
  form_no?: string
  set?: Record<string, unknown>
  complete?: boolean
  /** match_line */
  on?: string[]
  increment?: { column: string; by?: unknown }
  on_none?: string     // 'add_line' | 'goto:<step>' | 'error:<message>'
  on_many?: string
  /** lookup */
  lookup?: string
  filter?: string
  map?: Record<string, string>
  /** fetch_lines / recalc_lines */
  source?: string
  select?: 'all' | 'ask'
  /** run_action */
  action?: string
  /** validate */
  scope?: 'form' | 'tree'
  /** fetch_url (inline): the step's own request is used; `step` names another step's */
  step?: string
  /** goto */
  goto?: string
  /** message */
  message?: string
  kind?: 'info' | 'success' | 'warning' | 'error'
  [key: string]: unknown
}

export type WizardContextKind = 'visual' | 'dashboard' | 'report' | 'record'

export interface WizardPickRow {
  /** wizard target → column of the clicked row */
  set: Record<string, string>
  then?: string   // 'goto:<step>'
}

export interface WizardContextPanel {
  kind: WizardContextKind
  obj_name: string
  title?: string
  /** criteria / prompt NAME → template */
  params?: Record<string, unknown>
  /** record: T column (db_name) → template */
  match?: Record<string, unknown>
  layout?: 'strip' | 'panel' | 'collapsed'
  refresh_on?: string[]
  pick_row?: WizardPickRow
}

export interface WizardReportBlock {
  obj_name: string
  /** prompt NAME → template */
  param_map?: Record<string, unknown>
  output_format?: string
}

/** `none`: only on a print step with a `file` block — the report is filed
 *  into its repository and not shown, downloaded or printed. */
export type WizardPrintDelivery = 'preview' | 'download' | 'print_dialog' | 'device' | 'none'

/** A print step's `file` block: the rendered report is FILED into a document
 *  repository (K) for the saved record (`wizard_file`), as the next version
 *  of that record's filing when there is one. Every value is read from the
 *  saved model on the server; templates are filled from the STORED record. */
export interface WizardFileBlock {
  repository: string
  /** Document type key in the repository (its retention, review, signatures). */
  doctype?: string
  /** Folder path in the repository ("Quality/Certificates"), resolved, never created. */
  folder?: string
  title?: string
  filename?: string
  /** Index field key → template. */
  metadata?: Record<string, unknown>
  /** The var the filing lands in (default `filed`): `{vars.filed.document_id}`. */
  as?: string
}

/** What `wizard_file` answers — `filed` is stored in `vars.<var>`. */
export interface WizardFiled {
  document_id: string
  version_id: string
  sha256: string
  size: number
  filename: string
  repository: string
  report: string
  txn_id: string
  new_document: boolean
}

/** execute(<wizard>, 'wizard_file', {step_id, txn_id, frame}). */
export interface WizardFileResponse {
  ok: boolean
  var?: string
  filed?: WizardFiled
  error?: string
  code?: string
}

/** execute(<wizard>, 'wizard_send', {step_id, txn_id, frame}). */
export interface WizardSendResponse {
  ok: boolean
  run_key?: string
  queued?: number
  skipped?: number
  error?: string
  code?: string
}

export type WizardToolKind = 'open' | 'assistant' | 'print' | 'action' | 'goto' | 'call'

export interface WizardTool {
  label: string
  icon?: string
  kind: WizardToolKind
  enabled_when?: string
  /** open — the transaction `open` block verbatim (default presentation 'drawer') */
  open?: OpenActionBinding
  /** assistant — the transaction `assistant` block verbatim */
  assistant?: { mode: 'intent' | 'prompt'; intent_id?: string; param_map?: Record<string, unknown>; prompt?: string; title?: string }
  /** print */
  report?: WizardReportBlock
  delivery?: WizardPrintDelivery
  copies?: number
  printer?: string
  /** action — a base-T ActionDef name */
  action?: string
  /** goto */
  goto?: string
  /** call — same block as a `call` step */
  call?: WizardCallBlock
}

export type WizardCallOutcome =
  | 'completed' | 'cancelled' | 'pending_approval' | 'pending_assignee' | 'failed'

export interface WizardReturnRoute {
  refresh?: WizardOp[]
  /** step id, 'same' (default) or 'next' */
  resume?: string
  goto?: string
  suspend?: boolean
  message?: string
}

export interface WizardCallBlock {
  wizard: string
  ask?: string
  /** child input NAME → template */
  with?: Record<string, unknown>
  lock_inputs?: string[]
  /** 'self' or a role that completes the child as a Work List task */
  run_as?: 'self' | { role: string }
  /** caller target → child output NAME */
  returns?: Record<string, string>
  on_return?: Partial<Record<WizardCallOutcome, WizardReturnRoute>>
}

export interface WizardScanParse {
  format?: 'gs1' | 'json' | 'kv' | 'delimited' | 'url' | 'raw'
  pattern?: string
  separators?: string
  sep?: string
  fields?: string[]
  params?: boolean
  name?: string
  map?: Record<string, unknown>
}

export interface WizardFetchRequest {
  connection?: string
  url?: string
  path?: string
  method?: 'GET' | 'POST'
  body?: unknown
  headers?: Record<string, string>
}

/** A credential-store row for a function's context (§4): `object` is a
 *  transaction declaring `transaction.credential_store`; `match` maps its
 *  columns to templates (a blank stored value is a wildcard). */
export interface WizardCredentialRef {
  object: string
  match?: Record<string, unknown>
}

/** One call of a declared wizard function (`functions[]`). */
export interface WizardFunctionCall {
  name: string
  /** argument NAME → template, rendered on the SERVER into `args` */
  input?: Record<string, unknown>
  credential?: WizardCredentialRef
}

/** A wizard function: Python at `src/wizard_models/<wizard>/fn__<name>.py`
 *  defining `run(args)`, run in the business-logic sandbox (§3). */
export interface WizardFunctionDecl {
  name: string
  description?: string
  /** hosts `api.http` may reach (beside the credential's endpoint host) */
  hosts?: string[]
}

/** A `done` / `fail` condition over a polled job's status JSON. */
export interface WizardPollCondition {
  /** minimal JSONPath: `$.a.b`, `$.items[0].state` */
  path: string
  in?: unknown[]
  equals?: unknown
  exists?: boolean
}

/** A `function` step whose long job the BROWSER watches (§5.1): the function
 *  returns `{poll: {url, headers?}, state?}`; the runner polls `url` until
 *  `done` (or `fail`), then the server runs `finish` once. */
export interface WizardPollBlock {
  interval_s?: number
  timeout_min?: number
  done: WizardPollCondition
  fail?: WizardPollCondition
  /** fetches the authoritative result with the credential (`api.state()`) */
  finish?: WizardFunctionCall
}

/** What `wizard_function` answers for a step with `poll` while the job runs. */
export interface WizardPollTicket {
  url: string
  headers?: Record<string, string>
  interval_s: number
  timeout_min: number
  expires_at?: string
  done: WizardPollCondition
  fail?: WizardPollCondition
}

/** A `redirect` step (§6): out to another website and back. */
export interface WizardRedirectBlock {
  /** builds the outside URL — `api.callback_url()` is where it must come back */
  prepare?: WizardFunctionCall
  /** or a URL template using `{run.callback_url}` */
  url?: string
  /** reads what came back (`api.payload()`); absent ⇒ a JSON / form body is the answer */
  receive?: WizardFunctionCall
  /** minutes the callback stays valid (default 30, max 1440) */
  timeout_min?: number
  /** caption of the button that opens the other site */
  button?: string
}

export interface WizardStep {
  id: string
  kind: WizardStepKind
  title?: string
  help?: string
  when?: string
  form_no?: string
  next?: WizardNext
  then?: WizardOp[]
  on_issue?: Array<{ code: string; goto?: string; message?: string; call?: WizardCallBlock }>
  context?: WizardContextPanel[]
  tools?: WizardTool[]
  auto_advance?: boolean
  back?: boolean
  required_when?: Record<string, string>

  // info / show
  body?: string
  acknowledge?: boolean
  content?: WizardContextPanel

  // select
  target?: string
  source?: {
    column?: string
    lookup?: string
    filter?: string
    values?: Array<{ data: unknown; display?: string }>
    records?: { filter?: Record<string, unknown>; columns?: string[] }
    line_source?: string
  }
  multi?: boolean
  display?: 'list' | 'tiles' | 'dropdown'
  auto_pick_single?: boolean
  columns?: string[]
  search?: boolean

  // input
  fields?: Array<string | { field: string; label?: string; help?: string; required?: boolean; default?: unknown; type?: string }>
  layout?: 'stack' | 'grid'

  // scan
  prompt?: string
  parse?: WizardScanParse
  alternatives?: WizardScanParse[]
  map?: Record<string, unknown>
  loop?: { until?: string; or_when?: string }
  expect?: string
  feedback?: { ok?: 'beep' | 'none'; error?: 'buzz' | 'none' }
  manual_entry?: boolean

  // grid
  editable?: string[]
  row_filter?: string
  allow_add?: boolean
  allow_delete?: boolean
  row_tools?: WizardTool[]

  // confirm
  show?: string[]
  validate?: boolean
  confirm?: { label?: string; password?: boolean }

  // fetch_url
  request?: WizardFetchRequest
  timeout_ms?: number
  retry?: { button?: string }
  on_error?: string
  lines?: { path: string; form_no?: string; map: Record<string, string> }

  // function (an answer mapped by `map` / `lines`, like fetch_url)
  function?: WizardFunctionCall
  /** function: the job it starts is watched from the browser */
  poll?: WizardPollBlock

  // redirect (its receive answer mapped by `map` / `lines`)
  redirect?: WizardRedirectBlock

  // auto
  do?: WizardOp[]

  // branch
  cases?: Array<{ when: string; goto: string }>
  else?: string

  // load
  match?: Record<string, unknown>
  on_found?: { mode?: 'edit'; goto?: string; set?: Record<string, unknown> }
  on_missing?: { mode?: 'add'; goto?: string; set?: Record<string, unknown> } | string
  on_many?: 'pick' | string

  // repeat
  over?: string
  order_by?: string[]
  until_step?: string

  // save
  warnings?: 'ask' | 'fail'
  reason?: string
  on_saved?: string
  on_pending_approval?: string

  // print
  report?: WizardReportBlock
  output_format?: string
  delivery?: WizardPrintDelivery
  copies?: number
  printer?: string
  required?: boolean
  /** print — file the rendered report into a document repository. */
  file?: WizardFileBlock

  // sign — `document` = the id of the print step that FILES the document;
  // signed through the repository's own signing (DMS), never a wizard path.
  document?: string

  // send — `action` (above) = the base T's send button; `template` = a
  // template id (absent ⇒ the button's default template).
  template?: string

  // action
  action?: string

  // call / handoff
  wizard?: string
  ask?: string
  with?: Record<string, unknown>
  lock_inputs?: string[]
  run_as?: 'self' | { role: string }
  returns?: Record<string, string>
  on_return?: Partial<Record<WizardCallOutcome, WizardReturnRoute>>
  require_saved?: boolean
}

export interface WizardModel {
  obj_name: string
  title?: string
  description?: string
  base_object: string
  line_form?: string
  mode: WizardMode
  locate?: { match: Record<string, unknown>; on_missing?: 'error' | 'add' | string }
  inputs?: WizardInput[]
  outputs?: Record<string, string>
  vars?: WizardVar[]
  start: string
  steps: WizardStep[]
  tools?: WizardTool[]
  finish?: { then?: 'restart' | 'close' | 'open_record' | string; message?: string }
  suspend?: 'memory' | 'server'
  safe_resume?: string
  presentation?: { density?: 'handheld' | 'desktop' | 'auto'; show_record_panel?: boolean; progress?: 'path' | 'count' }
  limits?: { max_loop_iterations?: number; max_call_depth?: number }
  scope?: { edit_when?: string }
  also_writes?: string[]
  /** offline-capable (Phase 4) */
  offline?: boolean
  /** Server functions the `function` / `redirect` steps call (§3). */
  functions?: WizardFunctionDecl[]
  /** Runs with nobody watching (a follow-up): Check refuses steps that need
   *  a person (`NOT_UNATTENDED`). */
  unattended?: boolean
  /** Server-computed (normaliser): sha256 of the canonical model. */
  fingerprint?: string
}

/** The file shape: `{"wizard": {...}}`. */
export interface WizardRoot {
  wizard: WizardModel
}

// ── Wire ─────────────────────────────────────────────────────────────────

/** Rides every request body the runner makes (beside `payload`). */
export interface WizardVia {
  obj_name: string
  run_id: string
}

/** The values a server-side wizard op may read (`{header.X}`, `{line.X}`,
 *  `{lines.X}`, `{var.X}`, `{input.X}`, `{run.X}`). */
export interface WizardFrameValues {
  header: Record<string, unknown>
  line?: Record<string, unknown> | null
  lines?: Record<string, Array<Record<string, unknown>>>
  vars?: Record<string, unknown>
  input?: Record<string, unknown>
  run?: Record<string, unknown>
}

export interface WizardSet {
  target: string
  value: unknown
}

/** execute(<wizard>, 'wizard_scan', {step_id, text, frame, alt?}) */
export interface WizardScanRequest {
  step_id: string
  text: string
  frame: WizardFrameValues
}
export interface WizardScanResponse {
  ok: boolean
  /** which parse rule matched (`alternatives[i].name` or index), null on no match */
  rule: string | null
  fields: Record<string, unknown>
  sets: WizardSet[]
  /** `expect` evaluated true (or absent) */
  expect_ok: boolean
  message?: string
}

/** execute(<wizard>, 'wizard_fetch', {step_id, frame}) — and the same shape
 *  from 'wizard_function' {step_id, frame} and 'wizard_redirect_status'. */
export interface WizardFetchResponse {
  ok: boolean
  status?: number
  sets: WizardSet[]
  lines?: { form_no: string; rows: Array<Record<string, unknown>> }
  error?: string
  /** a function's reported issue code (routes `on_issue`) */
  code?: string
  /** a function's message for the user (a toast) */
  message?: string
  warnings?: Array<{ message: string; code?: string }>
  /** `wizard_function` on a step with `poll`: the job was started */
  pending?: boolean
  poll?: WizardPollTicket
  /** `wizard_poll_check` / `wizard_poll_finish` / `wizard_redirect_status` */
  state?: string
}

/** execute(<wizard>, 'wizard_redirect_start', {step_id, run_id, frame}) */
export interface WizardRedirectStartResponse {
  ok: boolean
  url?: string
  method?: 'GET' | 'POST'
  /** POST: the fields of the auto-submitting form */
  form?: Record<string, string>
  expires_at?: string
  error?: string
  code?: string
}

/** execute(<wizard>, 'wizard_redirect_status', {step_id, run_id, frame}) */
export interface WizardRedirectStatusResponse extends WizardFetchResponse {
  state: 'none' | 'waiting' | 'received' | 'expired'
}

/** What a `fill` wizard hands back to the screen that opened it: the header
 *  values the run changed, and the rows it ADDED per top-level child form. */
export interface WizardFillResult {
  header: Record<string, unknown>
  lines: Record<string, Array<Record<string, unknown>>>
}

/** execute(<wizard>, 'wizard_confirm', {step_id, password}) — a `confirm`
 *  step declaring `confirm.password`: the server verifies the CALLER's own
 *  password through the host's confirmation provider (the one an action
 *  button's password confirmation uses). Never echoed. Wrong answers are
 *  bounded (`WIZARD_CONFIRM_LOCKED` + `retry_after` seconds); a host with no
 *  provider answers `WIZARD_CONFIRM_UNAVAILABLE`. */
export interface WizardConfirmRequest {
  step_id: string
  password: string
}
export interface WizardConfirmResponse {
  ok: boolean
  verified: boolean
  code?: 'WIZARD_CONFIRM_LOCKED' | 'WIZARD_CONFIRM_UNAVAILABLE' | string
  message?: string
  attempts_left?: number
  retry_after?: number
}

export type WizardRunStatus =
  | 'active' | 'suspended' | 'waiting' | 'completed' | 'cancelled' | 'expired'

/** One frame of a run's stack (persisted as-is by the run store). */
export interface WizardFrame {
  wizard: string
  fingerprint?: string
  cursor: string
  mode: WizardMode
  txn_id?: string | null
  record: { header: Record<string, unknown>; details: Record<string, Array<Record<string, unknown>>> }
  vars: Record<string, unknown>
  input: Record<string, unknown>
  /** index into details[line_form] of the current line, or null */
  line_index?: number | null
  line_form?: string
  history: string[]
  /** repeat state per repeat-step id */
  repeat?: Record<string, { queue: number[]; pos: number }>
  /** the call this frame is waiting on (parent frames only) */
  pending_call?: {
    step_id: string; outcome?: WizardCallOutcome; outputs?: Record<string, unknown>
    /** Client: where `resume: "same"` / `"next"` land (the step on screen when
     *  the call started, and the calling step's own next). */
    resume_same?: string
    resume_next?: string | null
    /** Client: the call block itself, when it came from a tool / on_issue
     *  (a call STEP is found by `step_id`). */
    call?: WizardCallBlock
  }
  save_status?: 'none' | 'saved' | 'pending_approval'
  locked?: string[]

  // ── Client bookkeeping (additive; persisted as-is by the run store) ──
  /** Values steps captured — `{step.ID.value}`. */
  steps?: Record<string, unknown>
  /** Entry snapshots of the interactive steps visited, newest last (Back).
   *  Cleared at a boundary Back may not cross (a save, a print, a call). */
  back_stack?: WizardSnapshot[]
  /** Visits per step id — bounded by `limits.max_loop_iterations`. */
  visits?: Record<string, number>
  /** Changed since it was loaded / last saved. */
  dirty?: boolean
  /** Header protect flags item_change returned (form key → 1 lock / 0 unlock). */
  protect?: Record<string, 0 | 1>
  /** The run trail (§10.8): steps, scans, prints, calls, saves, issues. */
  trail?: WizardTrailEntry[]
  /** Restarted at `safe_resume` because its model changed while suspended. */
  model_changed?: boolean
  /** `fill` wizards: the header as the opening screen handed it over — what
   *  the finish diffs against (`WizardFillResult.header`). */
  fill_seed?: Record<string, unknown>
}

/** What Back restores: the frame as it was when a step was entered. */
export interface WizardSnapshot {
  cursor: string
  record: WizardFrame['record']
  vars: Record<string, unknown>
  steps?: Record<string, unknown>
  line_index?: number | null
  line_form?: string
  repeat?: WizardFrame['repeat']
  txn_id?: string | null
  save_status?: WizardFrame['save_status']
  dirty?: boolean
  protect?: Record<string, 0 | 1>
  /** The frame as the user LEFT the step (their input, before its `then`
   *  and what followed) — what Back shows when it returns here. */
  left?: Omit<WizardSnapshot, 'left'>
}

export interface WizardTrailEntry {
  at: string
  kind: 'step' | 'scan' | 'print' | 'file' | 'sign' | 'send' | 'call' | 'save' | 'issue' | 'finish'
  step?: string
  detail?: string
}

export interface WizardRun {
  run_id: string
  wizard: string
  status: WizardRunStatus
  stack: WizardFrame[]
  /** `approval` — a child's save is staged (`ref` = the approval instance id);
   *  `assignee` — a child runs as a Work List task for `role` (`wizard` = the
   *  child wizard, `title` its title); `ready` — the server recorded the
   *  outcome (`outcome`, on the parent frame's `pending_call`) and the run is
   *  `suspended`, waiting for its owner to resume it. */
  waiting_on?: {
    type: 'approval' | 'assignee' | 'ready'; ref?: string; role?: string
    wizard?: string; title?: string; outcome?: WizardCallOutcome
  } | null
  user_key?: string
  title?: string
  device?: string
  created_at?: string
  updated_at?: string
  expires_at?: string
}

/** execute(<wizard>, 'wizard_run_save', {run}) → {ok, run_id}
 *  execute(<wizard>, 'wizard_run_load', {run_id}) → {ok, run}
 *  execute(<wizard>, 'wizard_run_list', {all?: boolean}) → {ok, runs: WizardRun[] (no stack)}
 *  execute(<wizard>, 'wizard_run_discard', {run_id}) → {ok}
 *  execute(<wizard>, 'wizard_model', {}) → {ok, wizard: WizardModel, base: TransactionModel} */
/** execute(<wizard>, 'wizard_run_load', {run_id}) → who the caller is to it:
 *  `owner`, `manager` (manage_runs), or `assignee` (the role a `run_as` child
 *  was handed to — runs the TOP frame only and hands it back). */
export type WizardRunAccess = 'owner' | 'manager' | 'assignee'
export interface WizardRunLoadResponse {
  ok: boolean
  run: WizardRun
  access?: WizardRunAccess
}

/** execute(<child wizard>, 'wizard_run_handback', {run_id, outcome, outputs?})
 *  → {ok, run_id, status: 'suspended'} — the assignee finished (or cancelled /
 *  failed) the child; the server records the outcome on the parent frame. */
export interface WizardRunHandbackRequest {
  run_id: string
  outcome: 'completed' | 'cancelled' | 'failed'
  outputs?: Record<string, unknown>
}

/** execute(<wizard>, 'wizard_run_resume', {run_id}) → {ok, run}: activates a
 *  `suspended` run; a `waiting` one comes back unchanged. */

export interface WizardRunListResponse {
  ok: boolean
  runs: WizardRun[]
}

/** execute(<base T>, 'validate_tree', {header, details, txn_id?}) */
export interface ValidateTreeIssue {
  code?: string | null
  field?: string | null
  form_no?: string | null
  row?: number | null
  message: string
  type: 'E' | 'W' | 'P'
}
export interface ValidateTreeResponse {
  ok: boolean
  errors: ValidateTreeIssue[]
  warnings: ValidateTreeIssue[]
}

// ── Host seam ────────────────────────────────────────────────────────────

/** What a context panel / `show` step asks the host to draw. */
export interface WizardObjectSpec {
  objType: 'V' | 'D' | 'R' | 'T'
  objName: string
  params: Record<string, unknown>
  /** T record card: resolved match */
  match?: Record<string, unknown>
  layout: 'strip' | 'panel' | 'collapsed' | 'full'
  title?: string
  /** Bumped when the panel must re-run (refresh_on / after a call / save). */
  refreshKey: number
  /** Table-type visuals: a row was clicked (pick_row). */
  onPickRow?: (row: Record<string, unknown>) => void
}

export interface WizardPrintRequest {
  objName: string
  params: Record<string, unknown>
  outputFormat?: string
  delivery: WizardPrintDelivery
  copies?: number
  printer?: string
  title?: string
  /** The wizard run asking (device printing records it in the job history —
   *  never a grant). */
  via?: WizardVia
  /** `required: true` on the print step: resolve only once the job is
   *  delivered (a device print waits for the printer's answer). */
  required?: boolean
}

export interface WizardHost {
  project: string
  /** The runtime identity, for `{user.id}` / `{user.name}` (client-side
   *  templates only — the server never trusts it). */
  user?: { id?: string; name?: string }
  /** The saved, server-normalised wizard model (render read: RUN_APP only). */
  loadWizard(objName: string): Promise<WizardModel>
  /** The base transaction model (render read). */
  loadTxnModel(objName: string): Promise<TransactionModel>
  /** A screen runtime whose EVERY request carries `wizard: via` in its body
   *  (execute / execute-batch / lookup / resolve-defaults). The runner mounts
   *  the shared form stack on `runtime.txn`. */
  screenRuntime(via: WizardVia): TxnScreenRuntime
  /** Draw a V / D / R / T-card inside the wizard (context panels, `show`).
   *  Absent ⇒ panels render a "not available here" note. */
  renderObject?(spec: WizardObjectSpec): ReactNode
  /** `open` tools. */
  openObject?(req: OpenActionRequest): void
  /** `assistant` tools. */
  openAssistant?(req: AssistantActionRequest): void
  /** Print a report (render job → deliver). Resolves when delivered (a
   *  device print that is only queued resolves unless `req.required`);
   *  rejects with the printer's own error. */
  printReport?(req: WizardPrintRequest): Promise<void>
  /** A document repository's client (`sign` steps sign the filed document
   *  through it — the repository's own signing endpoints and rights).
   *  Absent, or a client without `sign`, ⇒ the step says signing is not
   *  available here. */
  dmsClient?(repository: string): DmsClient
  /** True while the device works OFFLINE (Phase 4): the runner then parses
   *  scans itself (scanParse.ts) and keeps runs in device storage. Absent ⇒
   *  online. */
  isOffline?(): boolean
  /** Whether the caller may use an object (hides tools / panels they may not). */
  canUse?(objType: string, objName: string, right: string): Promise<boolean>
  /** `handoff`: replace this wizard with another one. */
  startWizard?(wizard: string, inputs: Record<string, unknown>): void
  /** The run ended and the host should leave the wizard (finish.then 'close'). */
  close?(result: { outcome: WizardCallOutcome; txnId?: string | null }): void
  /** Open the saved record on its own screen (finish.then 'open_record'). */
  openRecord?(objName: string, txnId: string): void
  /** Toasts. */
  notify(kind: 'success' | 'error' | 'info' | 'warning', message: string): void
}

/** Props of the runner every host mounts. */
export interface WizardRunnerProps {
  host: WizardHost
  wizard: string
  /** Launch inputs (URL params / action param_map / handoff `with`). */
  inputs?: Record<string, unknown>
  /** Resume a persisted run instead of starting one. */
  resumeRunId?: string
  /** Embedded (drawer) vs. full screen. */
  presentation?: 'screen' | 'drawer'
  /** `fill` wizards: the opening screen's buffer — the run's record starts
   *  as it (header + the rows of each top-level child form). */
  seed?: { header: Record<string, unknown>; details: Record<string, Array<Record<string, unknown>>> }
  /** `fill` wizards: called once when the run completes, before `host.close`. */
  onFill?: (result: WizardFillResult) => void
  /** Every committed change of the run (the designer Preview's inspector).
   *  Read-only: a host must not feed it back into the runner. */
  onState?: (state: WizardRunnerState) => void
}

/** What `onState` reports: the run, its top frame, the template scope the
 *  step on screen sees, and the issues last shown. */
export interface WizardRunnerState {
  run: WizardRun | null
  frame: WizardFrame | null
  scope: WizardFrameValues | null
  step: string | null
  phase: string
  issues: ValidateTreeIssue[]
}
